import {
  faBagShopping,
  faCircle,
  faLocationDot,
  faShapes,
  faStore,
  faTruckFast,
  faUsers,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { Product } from '@shared/components/ui/Product';
import { useCountryTranslation } from '@shared/hooks/useCountryTranslation';
import React, { useRef } from 'react';
import { Badge, Checkbox } from '../../../../shared/components/ui';
import { GroupingField, PendingOrderWithIds } from '../../hooks/usePendingOrders';
import { OrderDetailsModal, OrderDetailsModalRef } from '../modals/OrderDetailsModal';

interface PendingOrderCardProps {
  order: PendingOrderWithIds;
  index: number;
  groupingFields: Set<GroupingField>;
  isSelected: boolean;
  onToggleSelection: () => void;
}

const PendingOrderCardComponent: React.FC<PendingOrderCardProps> = ({
  order,
  index,
  groupingFields,
  isSelected,
  onToggleSelection,
}) => {
  // 🎯 Referencia al modal
  const modalRef = useRef<OrderDetailsModalRef>(null);

  // 🌍 Hook para traducir países
  const { getCountryName } = useCountryTranslation();

  // 🎯 Función para abrir el modal con los datos del pedido
  const handleOpenModal = () => {
    modalRef.current?.openWithOrderIds({
      orderIds: order.order_ids || [order.order_id],
      productName: order.product.name,
      totalQuantity: order.qty,
    });
  };

  return (
    <>
      <div
        key={`${order.order_id}-${order.product.id}-${index}`}
        className="border-b border-border last:border-b-0 py-2 pl-4 pr-2 bg-card hover:bg-accent/30 transition-colors"
      >
        {/* Layout Desktop - Todo en una fila */}
        <div className="hidden md:flex items-center justify-between">
          {/* Checkbox + Información del producto */}
          <div className="flex items-center gap-4 flex-1">
            {/* Checkbox separado */}
            <Checkbox checked={isSelected} onCheckedChange={onToggleSelection} />

            {/* Componente Product sin checkbox */}
            <Product
              id={order.product.id}
              src={order.product.image_hash}
              name={order.product.name}
              code={order.product.code}
              showImageModal={true}
              enableLineClamp={false}
            />
          </div>

          {/* Cantidad y botón de detalles */}
          <div className="text-right mx-4 flex items-center gap-4 text-sm">
            <span className="">
              <FontAwesomeIcon icon={faBagShopping} className="mr-1" /> {order.qty}
            </span>

            {/* Botón para mostrar order IDs */}
            <Button variant="outline" size="sm" onClick={handleOpenModal} className="text-xs">
              {order.order_ids?.length || 1} pedidos
            </Button>
          </div>
        </div>

        {/* Layout Mobile - Tres filas */}
        <div className="md:hidden space-y-3">
          {/* Primera fila: Checkbox + Producto */}
          <div className="flex items-center gap-3">
            <Checkbox checked={isSelected} onCheckedChange={onToggleSelection} />
            <div className="flex-1">
              <Product
                id={order.product.id}
                src={order.product.image_hash}
                name={order.product.name}
                code={order.product.code}
                showImageModal={true}
                enableLineClamp={false}
              />
            </div>
          </div>

          {/* Segunda fila: Cantidad y botón de pedidos */}
          <div className="flex items-center justify-between">
            <span className="text-sm">
              <FontAwesomeIcon icon={faBagShopping} className="mr-1" /> {order.qty}
            </span>
            <Button variant="outline" size="sm" onClick={handleOpenModal} className="text-xs">
              {order.order_ids?.length || 1} pedidos
            </Button>
          </div>

          {/* Tercera fila: Badges de información según agrupación */}
          {groupingFields.size > 0 && (
            <div className="flex flex-wrap gap-2 text-[13px]">
              {/* Mostrar badge de mensajería solo si se agrupa por mensajería */}
              {groupingFields.has('courier') && (
                <Badge variant="outline">
                  <FontAwesomeIcon icon={faTruckFast} className="mr-2" />
                  {order.courier_id}
                </Badge>
              )}

              {/* Mostrar badge de país solo si se agrupa por país */}
              {groupingFields.has('country') && (
                <Badge variant="outline">
                  <FontAwesomeIcon icon={faLocationDot} className="mr-2" />
                  {getCountryName(order.country)}
                </Badge>
              )}

              {/* Mostrar badge de tienda solo si se agrupa por tienda */}
              {groupingFields.has('shop') && (
                <Badge variant="outline">
                  <FontAwesomeIcon icon={faStore} className="mr-2" />
                  {order.shop}
                </Badge>
              )}

              {/* Mostrar badge de tipo de pedido solo si se agrupa por tipo de pedido */}
              {groupingFields.has('order_type') && (
                <Badge variant="outline">
                  {order.order_type === 'simple' ? (
                    <FontAwesomeIcon icon={faCircle} className="mr-2" />
                  ) : (
                    <FontAwesomeIcon icon={faShapes} className="mr-2" />
                  )}
                  {order.order_type === 'simple'
                    ? 'Simple'
                    : order.order_type === 'multiple'
                      ? 'Múltiple'
                      : order.order_type}
                </Badge>
              )}

              {/* Mostrar badge de cliente solo si se agrupa por cliente */}
              {groupingFields.has('team_name') && (
                <Badge variant="outline">
                  <FontAwesomeIcon icon={faUsers} className="mr-2" />
                  {order.team_name}
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Badges de información según agrupación - Solo Desktop */}
        {groupingFields.size > 0 && (
          <div className="hidden md:flex flex-wrap gap-2 text-[13px] mt-2 border-t border-border pt-2">
            {/* Mostrar badge de mensajería solo si se agrupa por mensajería */}
            {groupingFields.has('courier') && (
              <Badge variant="outline">
                <FontAwesomeIcon icon={faTruckFast} className="mr-2" />
                {order.courier_id}
              </Badge>
            )}

            {/* Mostrar badge de país solo si se agrupa por país */}
            {groupingFields.has('country') && (
              <Badge variant="outline">
                <FontAwesomeIcon icon={faLocationDot} className="mr-2" />
                {getCountryName(order.country)}
              </Badge>
            )}

            {/* Mostrar badge de tienda solo si se agrupa por tienda */}
            {groupingFields.has('shop') && (
              <Badge variant="outline">
                <FontAwesomeIcon icon={faStore} className="mr-2" />
                {order.shop}
              </Badge>
            )}

            {/* Mostrar badge de tipo de pedido solo si se agrupa por tipo de pedido */}
            {groupingFields.has('order_type') && (
              <Badge variant="outline">
                {order.order_type === 'simple' ? (
                  <FontAwesomeIcon icon={faCircle} className="mr-2" />
                ) : (
                  <FontAwesomeIcon icon={faShapes} className="mr-2" />
                )}
                {order.order_type === 'simple'
                  ? 'Simple'
                  : order.order_type === 'multiple'
                    ? 'Múltiple'
                    : order.order_type}
              </Badge>
            )}

            {/* Mostrar badge de cliente solo si se agrupa por cliente */}
            {groupingFields.has('team_name') && (
              <Badge variant="outline">
                <FontAwesomeIcon icon={faUsers} className="mr-2" />
                {order.team_name}
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Modal fuera del div principal para evitar problemas de renderizado */}
      <OrderDetailsModal ref={modalRef} />
    </>
  );
};

// 🎯 Memorizar el componente para evitar re-renders innecesarios
export const PendingOrderCard = React.memo(PendingOrderCardComponent, (prevProps, nextProps) => {
  // Solo re-renderizar si cambian estas props específicas
  return (
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.order.qty === nextProps.order.qty &&
    prevProps.order.product.id === nextProps.order.product.id &&
    prevProps.order.team_name === nextProps.order.team_name &&
    prevProps.groupingFields === nextProps.groupingFields &&
    prevProps.index === nextProps.index
  );
});
